/**
 * The interface language. English is the source: every string is written in English and
 * wrapped in t() ("Level {n}" with params); a Turkish table (./tr/*.ts, one file per area of
 * the interface, English -> Turkish) replaces it when Turkish is chosen. A string missing from
 * the table shows in English. Data names (items, monsters, skills...) are localized once at
 * start in ./data.ts. The choice lives in localStorage; changing it reloads the page.
 *
 * The website's pages have an address per language (/database/ and /tr/database/, built by
 * seo.ts) so search engines index both: there the address decides, a visitor who prefers
 * Turkish is sent to the /tr/ copy, and the switch goes to the other address. The game and the
 * account page keep one address and follow the saved choice. The choice is also kept in the
 * wc_lang cookie, which lets the web server (deploy/Caddyfile) send a visitor to their copy
 * before any page loads.
 */
export type Lang = "en" | "tr";

export const LANGS: { id: Lang; name: string }[] = [
  { id: "en", name: "English" },
  { id: "tr", name: "Türkçe" },
];

const KEY = "wc.lang";
/** The same choice for the web server (deploy/Caddyfile reads it), a year long. */
const COOKIE = "wc_lang";

function cookieLang(): Lang | null {
  const m =
    typeof document === "undefined" ? null : /(?:^|; )wc_lang=(en|tr)/.exec(document.cookie);
  return (m?.[1] as Lang | undefined) ?? null;
}

function writeCookie(l: Lang) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE}=${l}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

function savedLang(): Lang | null {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "en" || saved === "tr") return saved;
  } catch {
    // Storage blocked: no saved choice.
  }
  return null;
}

/** Website pages with a Turkish copy under /tr/ (seo.ts PAGES). */
const SITE_PAGES = [
  "/",
  "/database/",
  "/privacy/",
  "/terms/",
  "/guide/",
  "/guide/beginner/",
  "/guide/classes/",
  "/guide/pvp/",
  "/guide/leveling/",
  "/guide/gear/",
  "/guide/gold/",
  "/guide/social/",
  "/guide/classes/warrior/",
  "/guide/classes/rogue/",
  "/guide/classes/mage/",
  "/guide/classes/priest/",
  "/guide/crystal-clash/",
  "/guide/war/",
  "/guide/umbral-rift/",
  "/guide/bosses/",
  "/browser-mmorpg/",
  "/mobile-mmorpg/",
  "/pvp-mmorpg/",
  "/online-rpg/",
  "/free-mmorpg/",
  "/turkish-mmorpg/",
  "/faq/",
];
/** A website page with a Turkish copy: one of SITE_PAGES or a database page (dbPages.ts). */
const isSitePage = (p: string) =>
  SITE_PAGES.includes(p) || /^\/database\/(item|monster|zone)\/[a-z0-9-]+\/$/.test(p);
const path = typeof location === "undefined" ? "/" : location.pathname;
/** This page is a Turkish copy (/tr/...). */
const onTrPage = path === "/tr" || path.startsWith("/tr/");
/** The English page this one is, or null outside the website's language pairs. */
const sitePath = onTrPage ? path.slice(3) || "/" : isSitePage(path) ? path : null;

/** Crawlers see the page they asked for, never a redirect by language. */
const isBot = () => /bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent);

function preferred(): Lang {
  const saved = savedLang();
  if (saved) return saved;
  return navigator.language?.toLowerCase().startsWith("tr") ? "tr" : "en";
}

/** The address of a website page in a language. */
const addressOf = (enPath: string, l: Lang) =>
  `${l === "tr" ? `/tr${enPath}` : enPath}${location.search}${location.hash}`;

function detect(): Lang {
  const saved = savedLang();
  const hadCookie = cookieLang() !== null;
  // A choice saved before the cookie existed: hand it to the web server too.
  if (saved && cookieLang() !== saved) writeCookie(saved);
  if (onTrPage) {
    // The web server sent a Turkish browser here before it knew this visitor had picked
    // English: go back once, now that the cookie says so.
    if (saved === "en" && !hadCookie && sitePath !== null && !isBot())
      location.replace(addressOf(sitePath, "en"));
    return "tr";
  }
  if (sitePath !== null) {
    if (preferred() === "tr" && !isBot()) location.replace(addressOf(sitePath, "tr"));
    return "en";
  }
  return preferred();
}

const current: Lang = detect();

/** The language in use on this page. */
export function lang(): Lang {
  return current;
}

/** Switches the language and reloads so every screen is built in it. */
export function setLang(l: Lang) {
  try {
    localStorage.setItem(KEY, l);
  } catch {
    // Private mode: it lasts for this page only.
  }
  writeCookie(l);
  if (sitePath !== null) location.assign(addressOf(sitePath, l));
  else location.reload();
}

/** A website link on a Turkish page leads to the Turkish copy ("/database/" to "/tr/database/"). */
function localizeLinks(root: ParentNode) {
  if (!onTrPage) return;
  const links =
    root instanceof HTMLAnchorElement
      ? [root]
      : [...root.querySelectorAll<HTMLAnchorElement>("a[href^='/']")];
  for (const a of links) {
    const href = a.getAttribute("href") ?? "";
    const m = /^(\/[^?#]*)(.*)$/.exec(href);
    if (m && isSitePage(m[1] ?? "")) a.setAttribute("href", `/tr${m[1]}${m[2]}`);
  }
}

const modules = import.meta.glob<Record<string, string>>("./tr/*.ts", {
  eager: true,
  import: "default",
});
const TR: Record<string, string> = Object.assign({}, ...Object.values(modules));
const dict: Record<string, string> = current === "tr" ? TR : {};
/** HTML text spreads over lines: matched with every run of spaces and line breaks as one space. */
const squash = (s: string) => s.replace(/\s+/g, " ").trim();
const BY_TEXT = new Map(Object.entries(TR).map(([en, tr]) => [squash(en), tr]));

/**
 * Translates an English interface string. `{name}` placeholders take `params`, in both
 * languages (Turkish may put them in another order).
 */
export function t(en: string, params?: Record<string, string | number>): string {
  const s = dict[en] ?? en;
  return params
    ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m))
    : s;
}

/** Numbers the way the language writes them (1,500 or 1.500). */
/** A calendar date in the page's language: 30 Eylül 2026 / September 30, 2026. */
export function dateText(ms: number): string {
  return new Date(ms).toLocaleDateString(current === "tr" ? "tr-TR" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function num(n: number, digits = 0): string {
  return n.toLocaleString(current === "tr" ? "tr-TR" : "en-US", {
    maximumFractionDigits: digits,
  });
}

const ATTRS = ["placeholder", "title", "aria-label", "alt"] as const;

/** Swaps whole English text nodes and labels for their Turkish (static HTML and simple UI). */
export function translateDom(root: ParentNode) {
  if (current === "en") return;
  localizeLinks(root);
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walk.nextNode(); n; n = walk.nextNode()) {
    const raw = n.nodeValue ?? "";
    const text = squash(raw);
    if (!text || !/[A-Za-z]/.test(text)) continue;
    const tr = BY_TEXT.get(text);
    if (tr) n.nodeValue = raw.replace(raw.trim(), tr);
  }
  const els =
    root instanceof Element
      ? [root, ...root.querySelectorAll("*")]
      : [...root.querySelectorAll("*")];
  for (const el of els)
    for (const a of ATTRS) {
      const v = el.getAttribute(a);
      const tr = v ? BY_TEXT.get(squash(v)) : undefined;
      if (tr) el.setAttribute(a, tr);
    }
}

/**
 * Keeps translating whatever the interface adds later (panels built from HTML strings): a
 * safety net for plain labels; strings with numbers in them go through t().
 */
export function watchDom(root: Node = document.body) {
  if (current === "en") return;
  const tr = BY_TEXT.get(squash(document.title));
  if (tr) document.title = tr;
  translateDom(document.body);
  new MutationObserver((list) => {
    for (const m of list)
      for (const node of m.addedNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          const hit = BY_TEXT.get(squash(node.nodeValue ?? ""));
          if (hit)
            node.nodeValue = (node.nodeValue ?? "").replace((node.nodeValue ?? "").trim(), hit);
        } else if (node instanceof Element) translateDom(node);
      }
  }).observe(root, { childList: true, subtree: true });
}

/** A small EN / TR switch (site header, sign-in screens and the game menu). */
export function languageSwitch(): HTMLElement {
  const box = document.createElement("div");
  box.className = "lang-switch";
  box.setAttribute("role", "group");
  box.setAttribute("aria-label", t("Language"));
  for (const l of LANGS) {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = l.id.toUpperCase();
    b.title = l.name;
    b.classList.toggle("on", l.id === current);
    b.setAttribute("aria-pressed", String(l.id === current));
    b.addEventListener("click", () => {
      if (l.id !== current) setLang(l.id);
    });
    box.append(b);
  }
  return box;
}
